import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Check, Zap, Crown } from 'lucide-react';
import { Button } from './ui/button';

const plans = [
  {
    name: 'Por Uso',
    icon: Zap,
    price: '8,90',
    period: 'por utilização',
    description: 'Ideal para quem recebe encomendas ocasionalmente.',
    features: [
      'Cadastro gratuito',
      'Pague apenas quando usar',
      'Acesso a todos os lockers',
      'Notificações em tempo real',
      'Suporte via chat',
      'Sem fidelidade',
    ],
    cta: 'Começar Grátis',
    highlighted: false,
  },
  {
    name: 'Assinatura',
    icon: Crown,
    price: '59,90',
    period: '/mês',
    description: 'Perfeito para quem compra online frequentemente.',
    features: [
      'Uso ilimitado dos lockers',
      'Prioridade na escolha do locker',
      'Notificações em tempo real',
      'Suporte prioritário 24/7*',
      'Histórico completo de entregas',
      'Cancele quando quiser',
    ],
    cta: 'Assinar Agora',
    highlighted: true,
    badge: 'Mais Popular',
  },
];

export const PricingCards = ({ showTitle = true }) => {
  return (
    <section className="py-24 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {showTitle && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <span className="inline-block bg-lokker-lime/20 text-slate-700 text-sm font-medium px-4 py-2 rounded-sm mb-4">
              Planos para Pessoa Física
            </span>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-slate-900 mb-4">
              Escolha seu plano ideal
            </h2>
            <p className="text-lg text-slate-600">
              Flexibilidade para se adequar ao seu estilo de vida. Sem surpresas, sem taxas escondidas.
            </p>
          </motion.div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {plans.map((plan, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              data-testid={`pricing-card-${plan.name.toLowerCase().replace(/\s+/g, '-')}`}
              className={`relative bg-white rounded-sm overflow-hidden ${
                plan.highlighted
                  ? 'border-2 border-lokker-blue shadow-xl'
                  : 'border border-slate-200 shadow-sm'
              }`}
            >
              {/* Badge */}
              {plan.badge && (
                <div className="absolute top-0 right-0">
                  <div className="bg-lokker-blue text-white text-xs font-bold px-4 py-1 uppercase tracking-wider">
                    {plan.badge}
                  </div>
                </div>
              )}

              <div className="p-8">
                {/* Header */}
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-12 h-12 rounded-sm flex items-center justify-center ${
                    plan.highlighted ? 'bg-lokker-blue' : 'bg-slate-100'
                  }`}>
                    <plan.icon className={`w-6 h-6 ${
                      plan.highlighted ? 'text-white' : 'text-lokker-blue'
                    }`} />
                  </div>
                  <h3 className="font-heading font-bold text-2xl text-slate-900">
                    {plan.name}
                  </h3>
                </div>

                {/* Price */}
                <div className="mb-4">
                  <div className="flex items-baseline gap-1">
                    <span className="text-sm text-slate-500">R$</span>
                    <span className="font-heading font-bold text-5xl text-slate-900">
                      {plan.price}
                    </span>
                    <span className="text-slate-500">{plan.period}</span>
                  </div>
                </div>

                {/* Description */}
                <p className="text-slate-600 mb-6">
                  {plan.description}
                </p>

                {/* Features */}
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center gap-3">
                      <div className={`w-5 h-5 rounded-sm flex items-center justify-center ${
                        plan.highlighted ? 'bg-lokker-lime' : 'bg-slate-100'
                      }`}>
                        <Check className={`w-3 h-3 ${
                          plan.highlighted ? 'text-black' : 'text-lokker-blue'
                        }`} />
                      </div>
                      <span className="text-slate-700">{feature}</span>
                    </li>
                  ))}
                </ul>

                {/* CTA */}
                <Link to="/planos">
                  <Button
                    data-testid={`pricing-cta-${plan.name.toLowerCase().replace(/\s+/g, '-')}`}
                    className={`w-full py-6 text-base rounded-sm ${
                      plan.highlighted
                        ? 'btn-primary'
                        : 'bg-slate-900 text-white hover:bg-slate-800'
                    }`}
                  >
                    {plan.cta}
                  </Button>
                </Link>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Trust Badges */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="mt-12 text-center"
        >
          <div className="flex flex-wrap justify-center gap-8 text-sm text-slate-500">
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-green-500" />
              <span>Pagamento seguro</span>
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-green-500" />
              <span>Cancele quando quiser</span>
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-green-500" />
              <span>Suporte em português</span>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default PricingCards;
